/**
 * Shared hook for refreshing flight/room pricing via the recheck flows.
 * Returns the refresh function, loading state, and last result.
 * Optionally shows toast notifications on completion.
 */

import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';

import { useCallback, useMemo, useState } from 'react';

import { canRefreshPricing } from '#lib/can-refresh-pricing.js';
import { useApp, useItineraryState, useToastState, useTripsState } from '#states/hooks.js';


type FlowFn = (input: unknown) => Promise<{ data: Record<string, unknown> }>;

type Flows = Record<string, FlowFn | undefined>;

type ToastState = ReturnType<typeof useToastState>;

export interface RefreshPricingResult {
  available: boolean;
  priceChanged: boolean;
  partiallyAvailable?: boolean;
  /** Room rechecks only: the rates the supplier no longer sells, by the rate keys the room held. */
  unavailableRateKeys?: string[];
  error?: boolean;
}

/** Call a single recheck flow and optionally show a toast with the result. */
async function recheckOne(
  flows: Flows,
  toastState: ToastState,
  type: 'flight' | 'room',
  tripId: string,
  referenceId: string,
  label?: string,
  showToasts = true,
): Promise<RefreshPricingResult> {
  try {
    const result =
      type === 'flight'
        ? await flows.recheckFlightPrice?.({ tripId, flightOptionId: referenceId })
        : await flows.recheckRoomPrice?.({ tripId, roomOptionId: referenceId });

    const data = result?.data as { available: boolean; priceChanged: boolean; partiallyAvailable?: boolean } | undefined;
    if (data) {
      if (showToasts) {
        if (!data.available) {
          toastState.addToast({ title: `This ${type} is no longer available`, description: label, variant: 'destructive' });
        } else if (data.priceChanged) {
          toastState.addToast({ title: 'Pricing has been updated', description: label, variant: 'default' });
        } else {
          toastState.addToast({ title: 'Pricing is up to date', description: label, variant: 'success' });
        }
      }
      return data;
    }
    return { available: false, priceChanged: false, error: true };
  } catch (_error) {
    if (showToasts) {
      toastState.addToast({ title: 'Unable to refresh pricing', description: label, variant: 'destructive' });
    }
    return { available: false, priceChanged: false, error: true };
  }
}

interface UseRefreshPricingOptions {
  type: 'flight' | 'room';
  referenceId?: string | null;
  /** Whether the option is eligible for a price recheck. Defaults to true. */
  enabled?: boolean;
  /** Show toast notifications on completion. Defaults to true. */
  showToasts?: boolean;
  /** Display name of the flight/room shown as secondary text in toasts. */
  label?: string;
}

export function useRefreshPricing({ type, referenceId, enabled = true, showToasts = true, label }: UseRefreshPricingOptions) {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  const { features } = useApp('features') as { features: { flows: Record<string, unknown> } };
  const flows = features.flows as Flows;
  const toastState = useToastState();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastResult, setLastResult] = useState<RefreshPricingResult | null>(null);

  const canRefresh = !!tripId && !!referenceId && enabled;

  /** Rechecks, and resolves with the result too (undefined when there is nothing to recheck). */
  const refresh = useCallback(async (e?: React.MouseEvent): Promise<RefreshPricingResult | undefined> => {
    e?.stopPropagation();
    if (!tripId || !referenceId) return;
    setIsRefreshing(true);
    setLastResult(null);
    try {
      const result = await recheckOne(flows, toastState, type, tripId, referenceId, label, showToasts);
      setLastResult(result);
      return result;
    } finally {
      setIsRefreshing(false);
    }
  }, [tripId, referenceId, type, flows, toastState, showToasts, label]);

  return { canRefresh, isRefreshing, lastResult, refresh };
}

/**
 * Hook for refreshing pricing on multiple room items at once (e.g. a hotel stay with several rooms).
 * Deduplicates by referenceId and refreshes all unique rooms in parallel.
 */
export function useRefreshRoomsPricing(items: ItineraryItem[]) {
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  const itineraryItems = useItineraryState().items;
  const { features } = useApp('features') as { features: { flows: Record<string, unknown> } };
  const flows = features.flows as Flows;
  const toastState = useToastState();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshableRoomIds = useMemo(() => {
    const seen = new Set<string>();
    for (const item of items) {
      if (!item.referenceId || !item.roomOption || seen.has(item.referenceId)) continue;
      if (canRefreshPricing('room', item.roomOption, itineraryItems)) {
        seen.add(item.referenceId);
      }
    }
    return [...seen];
  }, [items, itineraryItems]);

  const canRefresh = !!tripId && refreshableRoomIds.length > 0;

  const refresh = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!tripId || refreshableRoomIds.length === 0) return;
    setIsRefreshing(true);
    try {
      await Promise.allSettled(
        refreshableRoomIds.map(async (roomOptionId) => {
          const label = items.find((i) => i.referenceId === roomOptionId)?.displayTitle;
          return recheckOne(flows, toastState, 'room', tripId, roomOptionId, label);
        }),
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [tripId, refreshableRoomIds, items, flows, toastState]);

  return { canRefresh, isRefreshing, refresh };
}
